import {
  Box,
  Button,
  Flex,
  Grid,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useBreakpointValue,
  VStack,
} from "@chakra-ui/react";
import { AppContext } from "AppProvider/AppContext";
import CommonModal from "components/common/CommonModal/CommonModal";
import CustomDateRangeMobile from "components/common/CustomDateRangeMobile/CustomDateRangeMobile";
import CustomDateRangePicker from "components/common/CustomDateRangePicker/CustomDateRangePicker";
import { Paginationlimit } from "components/common/functions/variable";
import Pagination from "components/common/Pagination/Pagination";
import TableLoader from "components/common/TableLoader/TableLoader";
import { useContext, useEffect, useState } from "react";
import { HiOutlineFilter, HiOutlineRefresh } from "react-icons/hi";
import { appRequest } from "routes";
import { formatDate } from "utils/dateUtils/dateUtils";
import CustomSelect from "../../common/CustomSelect/CustomSelect";
import useFindBuyerName from "../../constants/function";
import ReconciliationDetails from "./ReconciliationDetails";
import { initialDateRange } from "../../Dashboards/OrderDashboard/function/constant";
import ReconciliationCardView from "./ReconciliationCardView/ReconciliationCardView";

const ReconciliationTable = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [reconData, setReconData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [bapIdFilter, setBapIdFilter] = useState<any>("");
  const { bapIds } = useContext(AppContext);
  const [reconDetails, setReconDetails] = useState({});
  const [filterStatus, setFilterStatus] = useState("");
  const [orderIds, setOrderIds] = useState<any>();
  const [searchTerms, setSearchTerms] = useState<any>([]);
  const [orderIdOptions, setOrderIdOptions] = useState<any>([]);
  const isMobileView = useBreakpointValue({ base: true, md: false });
  const [dateRange, setDateRange] = useState<any>(initialDateRange);
  const { startDate, endDate } = dateRange;

  // Sample order data
  const findBuyerNameByBapId = useFindBuyerName();

  const handleOpenModal = (data: any) => {
    setIsOpen(true);
    setReconDetails(data);
  };

  const handlePageChange = (page: any) => {
    setCurrentPage(page.selected + 1);
  };

  const getAllReconData = async (currentP?: number) => {
    try {
      setLoading(true);
      let page = currentP ? currentP : currentPage;
      let limit = Paginationlimit;
      let bapFilter = bapIdFilter ? bapIdFilter : "";
      let orderIdSearch = orderIds === undefined ? "" : orderIds;
      const request: any = {
        page,
        limit,
        bapFilter,
        filterStatus,
      };

      if (orderIdSearch.length === 0) {
        request.startDate = startDate;
        request.endDate = endDate;
      }

      if (orderIdSearch.length > 0) {
        request.orderIdSearch = orderIdSearch;
      }

      const response: any = await appRequest(
        "recon",
        "reconciliations",
        request
      );

      if (response.status === "success") {
        setReconData(response?.data);
        setTotalPages(response?.totalPages);
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const getOrderIds = async () => {
    try {
      const response: any = await appRequest("seller", "orderIds", {});
      if (response.status === "success") {
        setOrderIdOptions(response?.data || []);
      }
    } catch (error) {
      console.log("error", error);
    }
  };
  const orderIdOption = orderIdOptions.map((item: any) => ({
    value: item?._id,
    label: item?._id,
  }));

  const handleStartDateChange = (startDate: Date) => {
    setDateRange((prevDateRange: any) => ({
      ...prevDateRange,
      startDate: new Date(startDate),
    }));
  };

  const handleEndDateChange = (endDate: Date) => {
    setDateRange((prevDateRange: any) => ({
      ...prevDateRange,
      endDate: new Date(endDate),
    }));
  };

  const handleDateChange = (item: any) => {
    const newStartDate = item.selection.startDate.toISOString();
    const newEndDate = item.selection.endDate.toISOString();
    handleStartDateChange(newStartDate);
    handleEndDateChange(newEndDate);
  };
  const handleFilterChange = (e: any) => {
    setBapIdFilter(e.value);
  };
  const handleStatusChange = (e: any) => {
    setFilterStatus(e.value);
  };

  const bapNames = bapIds.map((value: any) => ({
    value: value?.bap_id,
    label: findBuyerNameByBapId(value?.bap_id),
  }));

  const filterStatusOptions = [
    { value: "RECONCILED", label: "Reconciled" },
    { value: "PENDING", label: "Pending" },
    { value: "DIFFED_HIGHER", label: "Diiffered Higher" },
    { value: "DIFFED_LESSER", label: "Diiffered Lesser" },
  ];

  useEffect(() => {
    getAllReconData();
    getOrderIds();
  }, [currentPage, bapIdFilter, endDate, filterStatus, orderIds]);

  const orderIdChange = (selectedOptions: any) => {
    const selectedIds = selectedOptions
      ? selectedOptions.map((option: any) => option.value)
      : [];
    setOrderIds(selectedIds); // Save as an array of strings
  };

  const handleRefreshAllData = () => {
    setDateRange(initialDateRange);
    setBapIdFilter("");
    setFilterStatus("");
    setSearchTerms([]);
    setOrderIdOptions([]);
    setOrderIds([]);
    // getAllReconData();
  };

  return (
    <Box
      p={5}
      rounded={"lg"}
      bg={"white"}
      shadow={"rgb(0 0 0 / 20%) 0px 0px 11px"}
    >
      <Flex
        justify={"space-between"}
        mb={2}
        direction={{ base: "column", md: "row" }} // Stacks on mobile and lines up on desktop
      >
        <Text fontWeight={700} fontSize={"xl"} color={"teal.400"} mb={2}>
          Reconciliations
        </Text>
        <Flex gap={2} direction={{ base: "column", md: "row" }}>
          <Box display={{ base: "none", lg: "block" }}>
            <CustomDateRangePicker
              dateFrom={dateRange.startDate && new Date(dateRange.startDate)}
              dateTo={dateRange.endDate && new Date(dateRange.endDate)}
              onStartDateChange={handleStartDateChange}
              onEndDateChange={handleEndDateChange}
              months={2}
            />
          </Box>
          <Box display={{ base: "block", lg: "none" }}>
            <CustomDateRangeMobile
              range={[dateRange]}
              onChange={handleDateChange}
            />
          </Box>
          <Flex gap={2} justify={"end"}>
            <Tooltip label="Refresh" hasArrow>
              <IconButton
                aria-label="Refresh"
                icon={<HiOutlineRefresh />}
                colorScheme="teal"
                variant="outline"
                borderWidth={2}
                _hover={{ bg: "teal.100" }}
                onClick={handleRefreshAllData}
                size={{ base: "sm", md: "md" }}
              />
            </Tooltip>
            <Popover>
              <PopoverTrigger>
                <IconButton
                  aria-label="Filter"
                  colorScheme="teal"
                  icon={<HiOutlineFilter />}
                  size={{ base: "sm", md: "md" }}
                />

              </PopoverTrigger>
              <Portal>
                <PopoverContent shadow={"lg"} borderWidth={2}>
                  <PopoverArrow />
                  <PopoverCloseButton />
                  <PopoverBody p={5}>
                    <VStack spacing={6} mt={2}>
                      <CustomSelect
                        options={orderIdOption}
                        label={"Order Ids"}
                        isMulti={true}
                        minH={searchTerms.length > 1 ? "64px" : "38px"}
                        onChange={orderIdChange}
                      />
                      <CustomSelect
                        options={bapNames}
                        label={"Buyer App"}
                        onChange={handleFilterChange}
                      />
                      <CustomSelect
                        options={filterStatusOptions}
                        label={"Filter By Status"}
                        onChange={handleStatusChange}
                      />
                    </VStack>
                  </PopoverBody>
                </PopoverContent>
              </Portal>
            </Popover>

          </Flex>
        </Flex>
      </Flex>

      {/* Mobile View Only */}
      {isMobileView ? (
        <Grid templateColumns="1fr" gap={4}>
          {reconData?.map((item: any, index) => (
            <ReconciliationCardView
              item={item}
              handleOpenModal={handleOpenModal}
              findBuyerNameByBapId={findBuyerNameByBapId}
              index={index}
            />
          ))}
        </Grid>
      ) : (
        //Desktop view only
        <>
          <TableContainer className="customScrollBar">
            <Table>
              <Thead>
                <Tr>
                  <Th>Order ID</Th>
                  <Th>Buyer App</Th>
                  <Th>Seller Ticket Fare</Th>
                  <Th>AFCS Ticket Fare</Th>
                  <Th>Order Date</Th>
                  <Th>Status</Th>
                </Tr>
              </Thead>
              <TableLoader
                loader={loading}
                show={reconData?.length}
                noDataText="No Data Available"
              >
                <Tbody>
                  {reconData?.map((item: any, index) => (
                    <Tr
                      key={index}
                      textAlign={"center"}
                      className={JSON.stringify(index)}
                    >
                      <Td
                        color={"blue.500"}
                        fontWeight={500}
                        onClick={() => handleOpenModal(item)}
                        cursor={"pointer"}
                        _hover={{ transform: "scale(1.05)" }}
                        transition={"0.3s ease-in-out"}
                        isTruncated
                        maxW={"10rem"}
                      >
                        {item?.order_id}
                      </Td>
                      <Td>{findBuyerNameByBapId(item?.bap_id)}</Td>
                      <Td>{item?.price_from_seller}</Td>
                      <Td>{item?.price_from_adaptor}</Td>
                      <Td>{formatDate(item?.order_time)}</Td>
                      <Td>
                        <Tag
                          size={"sm"}
                          colorScheme={
                            item.status === "RECONCILED"
                              ? "green"
                              : item.status === "PENDING"
                                ? "orange"
                                : "red"
                          }
                        >
                          {item.status}
                        </Tag>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </TableLoader>
            </Table>
          </TableContainer>
        </>
      )}

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        handlePageChange={handlePageChange}
      />
      <CommonModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        animate={true}
        width="62vw"
        body={
          <Box>
            <ReconciliationDetails reconDetails={reconDetails} />
          </Box>
        }
      />
    </Box>
  );
};

export default ReconciliationTable;
